(function () {
  const config = window.bmaxStoreSearchConfig || {};
  const NOSTO_ACCOUNT_ID = config.accountId || '';
  const NOSTO_API_URL = config.apiUrl || 'https://search.nosto.com/v1/graphql';
  const NOSTO_PUBLIC_SEARCH_BEARER = config.bearer || '';
  const SEARCH_URL = config.searchUrl || '/search';
  const CURRENCY_CODE = config.currencyCode || 'GBP';
  const CARD_BORDER_COLOR = config.cardBorderColor || '#192855';
  const STAR_ICON = config.starIcon || '';
  const STAR_HALF_ICON = config.starHalfIcon || '';
  const RECENT_ICON = config.recentIcon || '';
  const TRENDING_ICON = config.trendingIcon || '';
  const MIN_QUERY_LENGTH = 4;
  const SEARCH_PRODUCTS_SIZE = 16;
  const RECENT_KEY = 'bmax_recent_searches';
  const RECENT_MAX = 6;
  const REVIEW_RATING_THRESHOLD = Number(config.reviewRatingThreshold) || 4;

  const DEFAULT_TYPO_MESSAGE = 'Showing results for {correction}';
  const TYPO_VALUE_PATTERN = /^[a-z0-9 ]+$/i;

  function parseTypoMap(raw) {
    const map = Object.create(null);
    if (!raw || typeof raw !== 'string') return map;

    raw.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;

      const colonIndex = trimmed.indexOf(':');
      if (colonIndex <= 0) return;

      const typo = trimmed.slice(0, colonIndex).trim();
      const correction = trimmed.slice(colonIndex + 1).trim();
      if (!typo || !correction) return;
      if (!TYPO_VALUE_PATTERN.test(typo) || !TYPO_VALUE_PATTERN.test(correction)) return;

      map[typo.toLowerCase()] = correction;
    });

    return map;
  }

  const TYPO_MAP = parseTypoMap(config.typoMapRaw || '');

  function getTypoCorrection(query) {
    const key = String(query || '').trim().toLowerCase();
    if (!key) return null;

    const correction = TYPO_MAP[key];
    if (!correction || correction.toLowerCase() === key) return null;

    return correction;
  }

  function resolveRecentSearchTerm(term) {
    const value = (term || '').trim();
    if (!value) return '';
    return getTypoCorrection(value) || value;
  }

  function formatTypoMessage(correction) {
    const custom = typeof config.typoMessage === 'string' ? config.typoMessage.trim() : '';
    const template =
      custom && custom.includes('{correction}') ? custom : DEFAULT_TYPO_MESSAGE;
    return template.replace(/\{correction\}/g, correction);
  }

  function renderTypoNoticeHtml(query) {
    const correction = getTypoCorrection(query);
    if (!correction) return '';

    const correctionEscaped = escapeHtml(correction);
    const messageEscaped = escapeHtml(formatTypoMessage(correction));
    const html = messageEscaped.replace(
      correctionEscaped,
      `<span class="font-semibold">${correctionEscaped}</span>`
    );

    return `<p class="search-typo-notice mb-4 text-sm text-dark-blue mdlrg:text-base">${html}</p>`;
  }

  let popularSearchesLoaded = false;
  let searchAbortController = null;
  let lastQuery = '';

  const moneyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: CURRENCY_CODE,
  });

  function formatMoney(amount) {
    const value = Number(amount);
    if (!isFinite(value)) return '';
    return moneyFormatter.format(value);
  }

  function nostoHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (NOSTO_PUBLIC_SEARCH_BEARER) headers.Authorization = NOSTO_PUBLIC_SEARCH_BEARER;
    return headers;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (ch) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // Resize a Shopify CDN image url by appending a width parameter.
  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Mobile: pill button (like mega-menu brand links). Desktop (mdlrg+): plain underlined link.
  const SEARCH_LINK_CLASS =
    'search-button inline-flex items-center gap-1 text-body-sm text-nowrap whitespace-nowrap rounded-full border border-[#19285533] bg-white text-dark-blue py-[3px] px-[6px] transition-colors duration-300 hover:bg-blue hover:text-white mdlrg:inline mdlrg:border-0 mdlrg:rounded-none mdlrg:bg-transparent mdlrg:p-0 mdlrg:text-sm mdlrg:underline mdlrg:hover:bg-transparent mdlrg:hover:text-dark-blue mdlrg:hover:no-underline';

  // Build a search-term list item with a mobile-only leading icon.
  function searchLinkItem(term, icon) {
    const iconMarkup = icon
      ? `<span class="inline-flex shrink-0 mdlrg:hidden [&>svg]:h-[14px] [&>svg]:w-[14px]">${icon}</span>`
      : '';
    return `
      <li>
        <a
          href="${SEARCH_URL}?q=${encodeURIComponent(term)}"
          class="${SEARCH_LINK_CLASS}"
          data-record-term="${escapeHtml(term)}"
        >${iconMarkup}<span>${escapeHtml(term)}</span></a>
      </li>`;
  }

  // --- Recent searches (persisted in localStorage) ---
  function getRecentSearches() {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(stored) ? stored : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecentSearches(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {
      /* ignore quota / private mode errors */
    }
  }

  function addRecentSearch(term) {
    const value = resolveRecentSearchTerm(term);
    if (!value) return;
    const next = getRecentSearches().filter((t) => t.toLowerCase() !== value.toLowerCase());
    next.unshift(value);
    saveRecentSearches(next.slice(0, RECENT_MAX));
    renderRecentSearches();
  }

  function removeRecentSearch(term) {
    const value = (term || '').trim();
    if (!value) return;
    const next = getRecentSearches().filter((t) => t.toLowerCase() !== value.toLowerCase());
    saveRecentSearches(next);
    renderRecentSearches();
  }

  // Recent search row with a remove control shown on hover/focus.
  function recentSearchLinkItem(term) {
    const iconMarkup = RECENT_ICON
      ? `<span class="inline-flex shrink-0 mdlrg:hidden [&>svg]:h-[14px] [&>svg]:w-[14px]">${RECENT_ICON}</span>`
      : '';
    return `
      <li class="group/recent-item flex max-w-full items-center gap-0.5 max-mdlrg:inline-flex">
        <a
          href="${SEARCH_URL}?q=${encodeURIComponent(term)}"
          class="${SEARCH_LINK_CLASS}"
          data-record-term="${escapeHtml(term)}"
        >${iconMarkup}<span>${escapeHtml(term)}</span></a>
        <button
          type="button"
          class="js-remove-recent inline-flex shrink-0 items-center justify-center p-0.5 mdlrg:ml-1 opacity-100 mdlrg:opacity-0 transition-opacity duration-200 mdlrg:group-hover/recent-item:opacity-100 mdlrg:focus:opacity-100 text-dark-blue hover:text-pink mdlrg:hover:text-dark-blue"
          aria-label="Remove ${escapeHtml(term)}"
          data-term="${escapeHtml(term)}"
        >
          <span class="text-base leading-none mdlrg:hidden" aria-hidden="true">&times;</span>
          <span class="hidden mdlrg:inline text-xs underline hover:no-underline">Clear</span>
        </button>
      </li>`;
  }

  function refreshSearchProgressBars() {
    ['js-recent-searches-carousel', 'js-popular-searches-carousel'].forEach((id) => {
      const container = document.getElementById(id);
      if (!container) return;
      delete container.dataset.carouselInitialized;
      if (typeof window.initBMAXProgressBar === 'function') {
        window.initBMAXProgressBar(id);
      }
    });
  }

  function renderRecentSearches() {
    const wrapper = document.getElementById('recent-searches-wrapper');
    const list = document.getElementById('recent-searches');
    if (!wrapper || !list) return;

    const items = getRecentSearches();
    if (!items.length) {
      wrapper.classList.add('hidden');
      list.innerHTML = '';
      updateSearchTabs();
      return;
    }

    wrapper.classList.remove('hidden');
    list.innerHTML = items.map((term) => recentSearchLinkItem(term)).join('');

    updateSearchTabs();
    refreshSearchProgressBars();
  }

  // --- Mobile search tabs (Recent / Popular) ---
  function getActiveSearchTab() {
    const sidebar = document.getElementById('search-sidebar');
    return sidebar ? sidebar.getAttribute('data-active-tab') : 'popular';
  }

  function setActiveSearchTab(tab) {
    const sidebar = document.getElementById('search-sidebar');
    if (!sidebar) return;
    sidebar.setAttribute('data-active-tab', tab);
    document.querySelectorAll('.js-search-tab').forEach((btn) => {
      btn.setAttribute('data-active', btn.getAttribute('data-search-tab') === tab ? 'true' : 'false');
    });
    refreshSearchProgressBars();
  }

  // Show/hide the Recent tab based on availability and keep the active tab valid.
  function updateSearchTabs() {
    const recentTab = document.getElementById('search-tab-recent');
    const hasRecent = getRecentSearches().length > 0;
    if (recentTab) recentTab.classList.toggle('hidden', !hasRecent);
    if (!hasRecent && getActiveSearchTab() === 'recent') {
      setActiveSearchTab('popular');
    }
  }

  // Default to the Recent tab when there are recent searches, else Popular.
  function setDefaultSearchTab() {
    setActiveSearchTab(getRecentSearches().length ? 'recent' : 'popular');
  }

  // Trending searches (vertical list) via Nosto popularSearches API
  async function loadPopularSearches() {
    if (popularSearchesLoaded) return;
    popularSearchesLoaded = true;

    const list = document.getElementById('popular-searches');
    const wrapper = document.getElementById('popular-searches-wrapper');
    if (!list) return;

    try {
      const res = await fetch(NOSTO_API_URL, {
        method: 'POST',
        headers: nostoHeaders(),
        body: JSON.stringify({
          query: `
            query PopularSearches($accountId: String!, $query: String!, $popularSearches: InputSearchPopularSearches) {
              search(accountId: $accountId, query: $query, popularSearches: $popularSearches) {
                popularSearches {
                  hits { query }
                }
              }
            }`,
          variables: {
            accountId: NOSTO_ACCOUNT_ID,
            query: '',
            popularSearches: { size: 8, emptyQueryMatchesAll: true },
          },
        }),
      });
      const json = await res.json();
      const hits = (json?.data?.search?.popularSearches?.hits || [])
        .map((hit) => hit.query)
        .filter(Boolean);

      if (!hits.length) {
        if (wrapper) wrapper.classList.add('hidden');
        return;
      }

      list.innerHTML = hits.map((term) => searchLinkItem(term, TRENDING_ICON)).join('');
      refreshSearchProgressBars();
    } catch (e) {
      console.error('Popular searches failed', e);
      popularSearchesLoaded = false;
      if (wrapper) wrapper.classList.add('hidden');
    }
  }

  function showDefaultView() {
    const trending = document.getElementById('trending-main');
    const results = document.getElementById('search-results');
    if (trending) trending.classList.remove('hidden');
    if (results) {
      results.classList.add('hidden');
      results.innerHTML = '';
    }
  }

  function showResultsView() {
    const trending = document.getElementById('trending-main');
    const results = document.getElementById('search-results');
    if (trending) trending.classList.add('hidden');
    if (results) results.classList.remove('hidden');
  }

  function getCustomFieldValue(product, key) {
    const fields = product.customFields;
    if (!Array.isArray(fields)) return null;

    const field = fields.find((item) => item.key === key);
    return field?.value ?? null;
  }

  // Hides the rating display only; the product card still renders.
  // Mirrors snippets/mini-rating-star.liquid with is_product_card: true
  function renderRatingMarkup(ratingValue) {
    const rating = Number(ratingValue);
    if (!isFinite(rating) || rating <= 0 || rating < REVIEW_RATING_THRESHOLD) return '';

    const reviewRating = Math.round(rating * 100) / 100;
    const reviewRatingInteger = Math.floor(reviewRating);
    const reviewRatingDecimal = reviewRating % 1;

    let fullStars = '';
    for (let i = 0; i < reviewRatingInteger; i++) {
      fullStars += STAR_ICON;
    }
    if (reviewRatingDecimal > 0) {
      fullStars += STAR_HALF_ICON;
    }

    return `
        <p class="nosto-rating text-[0.875rem] flex items-center gap-1 text-dark-blue mb-1 order-2 sm:order-1">
          <span class="flex items-center gap-1">
            <span class="items-center gap-1 [&>svg]:h-[18px] [&>svg]:w-[18px] hidden xs:flex">${fullStars}</span>
            <span class="xs:hidden [&>svg]:h-[18px] [&>svg]:w-[18px]">${STAR_ICON}</span>
            <span class="text-[#192855] pt-[0.125rem]">(${escapeHtml(String(reviewRating))})</span>
          </span>
        </p>`;
  }

  function getProductUrl(product) {
    const url = product?.url || '';
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  function getVariantId(product) {
    const skuId = product?.skus?.[0]?.id;
    if (skuId != null && skuId !== '') return String(skuId);
    if (product?.variantId != null && product.variantId !== '') return String(product.variantId);
    return '';
  }

  // Re-init Swym after dynamic search results render.
  // Docs: https://developers.getswym.com/docs/swym-powered-buttons
  // Support: https://support.getswym.com/support/solutions/articles/9000238610-button-initialisation-for-infinte-dynamic-page-load
  function initSwymWishlistButtons(containerSelector = '#search-results', retryCount = 0) {
    const maxRetries = 2;
    try {
      const container = document.querySelector(containerSelector);
      if (!container) return;

      const needsInit = container.querySelector(
        '.swym-button:not(.swym-loaded):not(.swym-initialized):not([data-swym-initialized])'
      );
      if (!needsInit) return;

      const doInit = (swatObj) => {
        try {
          swatObj.initializeActionButtons(containerSelector);
        } catch (e) {
          console.warn('Swym init failed:', e);
        }
      };

      const swat = window._swat || window.swat;
      if (swat && typeof swat.initializeActionButtons === 'function') {
        doInit(swat);
        setTimeout(() => {
          const stillUninit = container.querySelector(
            '.swym-button:not(.swym-loaded):not(.swym-initialized):not([data-swym-initialized])'
          );
          if (stillUninit && retryCount < maxRetries) {
            doInit(swat);
          }
        }, 120);
      } else if (retryCount < maxRetries) {
        setTimeout(() => initSwymWishlistButtons(containerSelector, retryCount + 1), 200);
      } else {
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push((swatReady) => {
          doInit(swatReady);
        });
      }
    } catch (error) {
      console.warn('Failed to initialize Swym buttons:', error);
      if (retryCount < maxRetries) {
        setTimeout(() => initSwymWishlistButtons(containerSelector, retryCount + 1), 200);
      }
    }
  }

  // Product card mirroring snippets/rca-card-product.liquid (visual structure)
  function renderProductCard(product) {
    const url = product.url || '#';
    const name = product.name || '';
    const price = Number(product.price);
    const listPrice = Number(product.listPrice);
    const onSale = isFinite(listPrice) && listPrice > price;
    const image = product.imageUrl || product.thumbUrl;
    const productId = product.productId != null ? String(product.productId) : '';
    const variantId = getVariantId(product);
    const productUrl = getProductUrl(product);

    const rating = renderRatingMarkup(getCustomFieldValue(product, 'reviewscouk-rating'));

    const imageMarkup = image
      ? `<img
          src="${escapeHtml(sizedImage(image, 533))}"
          alt="${escapeHtml(name)}"
          loading="lazy"
          class="w-full h-full absolute top-0 left-0 object-contain object-center z-20"
        >`
      : '';

    const wishlistButton = productId
      ? `<button
          aria-label="Add to Wishlist"
          data-with-epi="true"
          class="nosto-wishlist swym-button swym-add-to-wishlist-view-product !p-1 product_${escapeHtml(productId)} flex-shrink-0"
          data-swaction="addToWishlist"
          data-product-id="${escapeHtml(productId)}"
          data-variant-id="${escapeHtml(variantId)}"
          data-product-url="${escapeHtml(productUrl)}"
        ></button>`
      : '';

    return `
      <a
        href="${escapeHtml(url)}"
        class="nosto-product rca-card-product block bg-white h-full rounded-[10px] md:rounded-2xl overflow-hidden relative cursor-pointer border-t-[8px] border-t-[var(--card-border-top-color)]"
        style="--card-border-top-color: ${escapeHtml(CARD_BORDER_COLOR)};"
      >
        <div class="rca-actions-container w-full h-auto flex justify-between items-center p-[10px] z-30">
          ${wishlistButton}
        </div>
        <div class="rca-image-container group relative overflow-hidden h-0 pb-[120%]">
          ${imageMarkup}
        </div>
        <div class="card-product-info flex flex-col pt-[0.625rem] px-2 pb-4 md:p-[0.625rem]">
          <p class="card-product-title order-1 sm:order-2 mb-1 md:mb-[0.625rem] font-semibold">${escapeHtml(name)}</p>
          ${rating}
          <div class="price order-3${onSale ? ' price--on-sale' : ''}">
            <div class="price__container">
              <div class="flex flex-wrap gap-[0.375rem] md:gap-[0.625rem]">
                <span class="price-item price-item--regular">${formatMoney(price)}</span>
                ${
                  onSale
                    ? `<span class="price-item--compare">${formatMoney(listPrice)}</span>
                       <div class="price-item--savings max-sm:basis-full">${formatMoney(listPrice - price)}</div>`
                    : ''
                }
              </div>
            </div>
          </div>
        </div>
      </a>`;
  }

  function renderSearchResults(search, query) {
    const results = document.getElementById('search-results');
    if (!results) return;

    const hits = search?.products?.hits || [];
    const total = search?.products?.total || hits.length;
    const typoNotice = renderTypoNoticeHtml(query);

    if (!hits.length) {
      results.innerHTML = `
        ${typoNotice}
        <p class="py-10 text-center text-dark-blue">
          No results found for <span class="font-semibold">${escapeHtml(query)}</span>
        </p>`;
      return;
    }

    results.innerHTML = `
      ${typoNotice}
      <div class="flex items-center justify-between mb-4 mdlrg:mb-5">
        <p class="h6 font-semibold mb-0">${total} result${total === 1 ? '' : 's'} for &ldquo;${escapeHtml(query)}&rdquo;</p>
        <a href="${SEARCH_URL}?q=${encodeURIComponent(query)}" class="button primary-button shrink-0">
          View all results
        </a>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 mdlrg:grid-cols-4 gap-2">
        ${hits.map(renderProductCard).join('')}
      </div>`;

    initSwymWishlistButtons('#search-results');
  }

  async function performSearch(query) {
    const results = document.getElementById('search-results');
    lastQuery = query;
    showResultsView();
    if (results) {
      results.innerHTML = '<p class="py-10 text-center text-sm text-dark-blue">Searching…</p>';
    }

    // Cancel any in-flight request so out-of-order responses can't overwrite newer ones.
    if (searchAbortController) searchAbortController.abort();
    searchAbortController = new AbortController();

    try {
      const res = await fetch(NOSTO_API_URL, {
        method: 'POST',
        headers: nostoHeaders(),
        signal: searchAbortController.signal,
        body: JSON.stringify({
          query: `
            query Search($accountId: String!, $query: String!, $products: InputSearchProducts) {
              search(accountId: $accountId, query: $query, products: $products) {
                products {
                  total
                  hits {
                    productId
                    name
                    url
                    imageUrl
                    thumbUrl
                    price
                    listPrice
                    availability
                    variantId
                    skus {
                      id
                    }
                    customFields {
                      key
                      value
                    }
                  }
                }
              }
            }`,
          variables: {
            accountId: NOSTO_ACCOUNT_ID,
            query: query,
            products: { size: SEARCH_PRODUCTS_SIZE },
          },
        }),
      });
      const json = await res.json();
      renderSearchResults(json?.data?.search, query);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('Search failed', e);
      if (results) {
        results.innerHTML = '<p class="py-10 text-center text-sm text-dark-blue">Something went wrong. Please try again.</p>';
      }
    }
  }

  function debounce(fn, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function handleSearchInput(value) {
    const query = (value || '').trim();
    if (query.length >= MIN_QUERY_LENGTH) {
      performSearch(query);
    } else {
      showDefaultView();
    }
  }

  function getSearchInput() {
    return document.querySelector('#nosto-form-search-selector .js-predictive-search-input');
  }

  function setSearchExpanded(expanded) {
    const input = getSearchInput();
    if (input) input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  // Pin panel height from the header bottom to the viewport bottom.
  function updateSearchPanelPosition() {
    const anchor = document.querySelector('.js-main-nav-menu');
    if (!anchor) return;
    const top = anchor.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--search-panel-top', `${top}px`);
  }

  function showPredictiveSearch() {
    updateSearchPanelPosition();
    const overlay = document.getElementById('predictive-search');
    document.body.setAttribute('data-search-enabled', 'true');
    document.body.style.overflow = 'hidden';
    if (overlay) overlay.removeAttribute('inert');
    setSearchExpanded(true);
    loadPopularSearches();
    renderRecentSearches();
    setDefaultSearchTab();

    const input = getSearchInput();
    if (input && document.activeElement !== input) {
      input.focus({ preventScroll: true });
    }
    if (input) handleSearchInput(input.value);
  }

  function hidePredictiveSearch() {
    const overlay = document.getElementById('predictive-search');
    document.body.setAttribute('data-search-enabled', 'false');
    document.body.style.overflow = '';
    if (overlay) overlay.setAttribute('inert', '');
    setSearchExpanded(false);
    getSearchInput()?.blur();
  }

  function isSearchOpen() {
    return document.body.getAttribute('data-search-enabled') === 'true';
  }

  // Navigate to the full search results page.
  function goToSearchResults(rawQuery) {
    const query = (rawQuery || '').trim();
    if (!query) return false;
    addRecentSearch(query);
    const params = new URLSearchParams({
      q: query,
      'options[prefix]': 'last',
    });
    window.location.assign(`${SEARCH_URL}?${params.toString()}`);
    return true;
  }

  function init() {
    const searchForm = document.getElementById('nosto-form-search-selector');
    const searchInput = getSearchInput();

    if (searchInput) {
      searchInput.addEventListener('focus', () => {
        if (!isSearchOpen()) showPredictiveSearch();
      });
      searchInput.addEventListener('click', () => {
        if (!isSearchOpen()) showPredictiveSearch();
      });
      // Enter → /search (explicit handler; Safari may not submit without a submit button).
      searchInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        goToSearchResults(searchInput.value);
      });
    }

    document.querySelectorAll('.js-clear-search').forEach((clearElem) => {
      clearElem.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const input = getSearchInput();
        if (!input) return;
        input.value = '';
        input.focus();
        handleSearchInput('');
      });
    });

    document.querySelectorAll('.js-close-search').forEach((closeElem) => {
      closeElem.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        hidePredictiveSearch();
      });
    });

    const debouncedInput = debounce((value) => handleSearchInput(value), 300);
    document.querySelectorAll('.js-predictive-search-input').forEach((input) => {
      input.addEventListener('input', (e) => debouncedInput(e.target.value));
    });

    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = searchForm.querySelector('.js-predictive-search-input');
        goToSearchResults(input ? input.value : '');
      });
    }

    document.addEventListener('mousedown', (e) => {
      if (!isSearchOpen()) return;
      if (e.target.closest('#predictive-search, #nosto-form-search-selector')) return;
      hidePredictiveSearch();
    });

    // Record recent searches on navigation away via a search/result link.
    const overlay = document.getElementById('predictive-search');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        const termLink = e.target.closest('[data-record-term]');
        if (termLink) {
          addRecentSearch(termLink.getAttribute('data-record-term'));
          return;
        }
        if (e.target.closest('#search-results a[href]')) {
          addRecentSearch(lastQuery);
        }
      });
    }

    const recentList = document.getElementById('recent-searches');
    if (recentList) {
      recentList.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.js-remove-recent');
        if (!removeBtn) return;
        e.preventDefault();
        e.stopPropagation();
        removeRecentSearch(removeBtn.getAttribute('data-term'));
      });
    }

    document.querySelectorAll('.js-search-tab').forEach((btn) => {
      btn.addEventListener('click', () => setActiveSearchTab(btn.getAttribute('data-search-tab')));
    });

    renderRecentSearches();
    setDefaultSearchTab();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isSearchOpen()) {
        hidePredictiveSearch();
      }
    });

    function onSearchLayoutChange() {
      if (isSearchOpen()) updateSearchPanelPosition();
    }

    window.addEventListener('resize', onSearchLayoutChange);
    window.addEventListener('scroll', onSearchLayoutChange, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
